import { useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Invoicing. A visit becomes a draft, the operator edits it, issues it, and optionally
 * hands it to Xero. Deliberately not an accounting screen — no ledgers, no reconciliation.
 */
export default function Invoices() {
  const visits = useQuery(api.visits.recent, { limit: 25 });
  const invoices = useQuery(api.invoices.list, {});
  const outstanding = useQuery(api.invoices.outstanding, {});
  const xero = useQuery(api.xero.status, {});

  const createFromVisit = useMutation(api.invoices.createFromVisit);
  const issue = useMutation(api.invoices.issue);
  const markPaid = useMutation(api.invoices.markPaid);
  const voidInvoice = useMutation(api.invoices.voidInvoice);
  const generatePdf = useAction(api.pdf.generateInvoice);
  const pushToXero = useAction(api.xero.pushInvoice);

  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function withBusy(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  }

  const unbilled = (visits ?? []).filter((v) => v.visit.completedAt && !v.invoice);

  return (
    <>
      <h1>Invoices</h1>

      {outstanding ? (
        <div className="tiles">
          <div className="tile">
            <span className="n">{outstanding.count}</span><span className="l">unpaid</span>
          </div>
          <div className="tile">
            <span className="n">{money(outstanding.totalCents)}</span><span className="l">outstanding</span>
          </div>
          <div className={`tile${outstanding.overdue ? ' alert' : ''}`}>
            <span className="n">{outstanding.overdue}</span><span className="l">over 30 days</span>
          </div>
          <div className="tile">
            <span className="n">{unbilled.length}</span><span className="l">visits to bill</span>
          </div>
        </div>
      ) : null}

      {message ? <div className="card"><p className="small" style={{ color: 'var(--alert)' }}>{message}</p></div> : null}

      {unbilled.length > 0 ? (
        <>
          <h2>Completed visits not yet billed</h2>
          <div className="card">
            <table>
              <thead><tr><th>Date</th><th>Client</th><th>Site</th><th>Items</th><th /></tr></thead>
              <tbody>
                {unbilled.map(({ visit, site, client, testCount }) => (
                  <tr key={visit._id}>
                    <td>{new Date(visit.startedAt).toLocaleDateString('en-AU')}</td>
                    <td>{client?.name ?? '—'}</td>
                    <td className="muted">{site?.name ?? '—'}</td>
                    <td>{testCount}</td>
                    <td>
                      <button
                        disabled={busy !== null}
                        onClick={() => withBusy(visit._id, async () => {
                          await createFromVisit({ visitId: visit._id });
                        })}
                      >
                        {busy === visit._id ? 'Creating…' : 'Create invoice'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <h2>All invoices</h2>
      <div className="card">
        <table>
          <thead>
            <tr><th>Number</th><th>Client</th><th>Total</th><th>Status</th><th>Xero</th><th /></tr>
          </thead>
          <tbody>
            {(invoices ?? []).map(({ invoice, client }) => {
              const id = invoice._id as Id<'invoices'>;
              return (
                <tr key={id}>
                  <td className="tag">{invoice.number}</td>
                  <td>{client?.name ?? '—'}</td>
                  <td>{money(invoice.total)}</td>
                  <td>
                    {invoice.status === 'void' ? <span className="pill">void</span> : invoice.status}
                  </td>
                  <td className="muted small">
                    {invoice.xeroInvoiceId ? 'sent' : xero?.connected ? '—' : 'not connected'}
                  </td>
                  <td>
                    <div className="row">
                      {invoice.status === 'draft' ? (
                        <button
                          disabled={busy !== null}
                          onClick={() => withBusy(id, async () => { await issue({ invoiceId: id }); })}
                        >
                          Issue
                        </button>
                      ) : null}
                      {invoice.status === 'issued' ? (
                        <>
                          <button
                            className="quiet"
                            disabled={busy !== null}
                            onClick={() => withBusy(id, async () => { await markPaid({ invoiceId: id }); })}
                          >
                            Mark paid
                          </button>
                          {xero?.connected && !invoice.xeroInvoiceId ? (
                            <button
                              className="quiet"
                              disabled={busy !== null}
                              onClick={() => withBusy(id, async () => {
                                const result = await pushToXero({ invoiceId: id });
                                if (!result.ok) throw new Error(result.error ?? 'Xero rejected it');
                                setMessage(`${invoice.number} sent to Xero as a draft.`);
                              })}
                            >
                              Send to Xero
                            </button>
                          ) : null}
                        </>
                      ) : null}
                      <button
                        className="quiet"
                        disabled={busy !== null}
                        onClick={() => withBusy(`${id}-pdf`, async () => {
                          const { url } = await generatePdf({ invoiceId: id });
                          if (url) window.open(url, '_blank', 'noopener');
                        })}
                      >
                        PDF
                      </button>
                      {invoice.status !== 'void' && invoice.status !== 'paid' ? (
                        <button
                          className="danger"
                          disabled={busy !== null}
                          onClick={() => withBusy(id, async () => {
                            await voidInvoice({ invoiceId: id, reason: 'Voided from console' });
                          })}
                        >
                          Void
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {invoices?.length === 0 ? (
              <tr><td colSpan={6} className="muted">
                No invoices yet. Complete a visit in the field app and it appears above.
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="card">
        <p className="muted small">
          Invoices push to Xero as <strong>drafts</strong>, never authorised — your accountant
          approves them there. TagVault never touches your ledgers.
        </p>
      </div>
    </>
  );
}
